// workAreas/hooks/useServices.js
import { useState, useRef } from 'react';
import { updateManagementWorkArea } from 'api';

export const useServices = (allWorkAreas, showSnackbar) => {
  const [servicesFormData, setServicesFormData] = useState({});
  const [originalServicesData, setOriginalServicesData] = useState({});
  const [servicesHasChanges, setServicesHasChanges] = useState({});
  const [isSavingServices, setIsSavingServices] = useState(false);
  const isSavingRef = useRef(false);

  const handleServicesFormChange = (workAreaSlug, newServicesData) => {
    setServicesFormData(prev => ({
      ...prev,
      [workAreaSlug]: newServicesData
    }));

    // Check if there are changes
    const originalData = originalServicesData[workAreaSlug];
    const hasChanges = originalData ? JSON.stringify(newServicesData) !== JSON.stringify(originalData) : false;

    setServicesHasChanges(prev => ({
      ...prev,
      [workAreaSlug]: hasChanges
    }));
  };

  // Separate save function for immediate saves (used by priority changes)
  const saveServices = async (workAreaSlug, servicesData) => {
    const workArea = allWorkAreas.find(wa => wa.slug === workAreaSlug);
    if (!workArea) {
      throw new Error("Work area not found");
    }

    // Format services for API
    const formattedServices = servicesData.map((service, index) => ({
      slug: service.slug,
      priority: index,
      description: service.description || '',
      is_featured: service.is_featured || false
    }));

    const requestData = {
      work_area: {
        slug: workAreaSlug,
        services: formattedServices
      }
    };

    const result = await updateManagementWorkArea(requestData, workAreaSlug);
    return result;
  };

  const handleSaveServices = async (workAreaSlug) => {
    const servicesData = servicesFormData[workAreaSlug];
    if (!servicesData) return;

    try {
      setIsSavingServices(true);
      isSavingRef.current = true;

      const workArea = allWorkAreas.find(wa => wa.slug === workAreaSlug);
      if (!workArea) {
        throw new Error("Work area not found");
      }

      // Format services for API
      const formattedServices = servicesData.map((service, index) => ({
        slug: service.slug,
        priority: index,
        description: service.description || '',
        is_featured: service.is_featured || false
      }));

      const requestData = {
        work_area: {
          slug: workAreaSlug,
          services: formattedServices
        }
      };

      const result = await updateManagementWorkArea(requestData, workAreaSlug);

      if (result && result.success) {
        showSnackbar("Services saved successfully", "success");

        // Update original data to match current form data
        setOriginalServicesData(prev => ({
          ...prev,
          [workAreaSlug]: [...servicesData]
        }));

        // Clear changes state
        setServicesHasChanges(prev => ({
          ...prev,
          [workAreaSlug]: false
        }));

      } else {
        showSnackbar(result?.error || "Failed to save services", "error");
      }
    } catch (error) {
      console.error("Error saving services:", error);
      showSnackbar("Failed to save services: " + error.message, "error");
    } finally {
      setIsSavingServices(false);
      isSavingRef.current = false;
    }
  };

  const handleCancelServices = (workAreaSlug) => {
    const originalData = originalServicesData[workAreaSlug];
    if (originalData) {
      setServicesFormData(prev => ({
        ...prev,
        [workAreaSlug]: [...originalData]
      }));

      setServicesHasChanges(prev => ({
        ...prev,
        [workAreaSlug]: false
      }));
    }
  };

  // Services reorder handlers - Save immediately and show toast
  const handleServiceMoveUp = async (workAreaSlug, serviceSlug) => {
    const services = servicesFormData[workAreaSlug] || [];
    const currentIndex = services.findIndex(service => service.slug === serviceSlug);
    if (currentIndex <= 0) return;

    const updatedServices = [...services];
    const temp = updatedServices[currentIndex];
    updatedServices[currentIndex] = updatedServices[currentIndex - 1];
    updatedServices[currentIndex - 1] = temp;

    // Update priorities
    updatedServices.forEach((service, idx) => {
      service.priority = idx;
    });

    // Update state immediately
    setServicesFormData(prev => ({
      ...prev,
      [workAreaSlug]: updatedServices
    }));

    // Save immediately and show toast
    try {
      setIsSavingServices(true);
      const result = await saveServices(workAreaSlug, updatedServices);
      if (result.success) {
        showSnackbar("Service priority updated successfully", "success");
        // Update original data to reflect saved state
        setOriginalServicesData(prev => ({
          ...prev,
          [workAreaSlug]: [...updatedServices]
        }));
        setServicesHasChanges(prev => ({
          ...prev,
          [workAreaSlug]: false
        }));
      } else {
        // Revert on failure
        setServicesFormData(prev => ({
          ...prev,
          [workAreaSlug]: services
        }));
        showSnackbar(result?.error || "Failed to update service priority", "error");
      }
    } catch (error) {
      // Revert on failure
      setServicesFormData(prev => ({
        ...prev,
        [workAreaSlug]: services
      }));
      showSnackbar("Failed to update service priority: " + error.message, "error");
    } finally {
      setIsSavingServices(false);
    }
  };

  const handleServiceMoveDown = async (workAreaSlug, serviceSlug) => {
    const services = servicesFormData[workAreaSlug] || [];
    const currentIndex = services.findIndex(service => service.slug === serviceSlug);
    if (currentIndex >= services.length - 1) return;

    const updatedServices = [...services];
    const temp = updatedServices[currentIndex];
    updatedServices[currentIndex] = updatedServices[currentIndex + 1];
    updatedServices[currentIndex + 1] = temp;

    // Update priorities
    updatedServices.forEach((service, idx) => {
      service.priority = idx;
    });

    // Update state immediately
    setServicesFormData(prev => ({
      ...prev,
      [workAreaSlug]: updatedServices
    }));

    // Save immediately and show toast
    try {
      setIsSavingServices(true);
      const result = await saveServices(workAreaSlug, updatedServices);
      if (result.success) {
        showSnackbar("Service priority updated successfully", "success");
        // Update original data to reflect saved state
        setOriginalServicesData(prev => ({
          ...prev,
          [workAreaSlug]: [...updatedServices]
        }));
        setServicesHasChanges(prev => ({
          ...prev,
          [workAreaSlug]: false
        }));
      } else {
        // Revert on failure
        setServicesFormData(prev => ({
          ...prev,
          [workAreaSlug]: services
        }));
        showSnackbar(result?.error || "Failed to update service priority", "error");
      }
    } catch (error) {
      // Revert on failure
      setServicesFormData(prev => ({
        ...prev,
        [workAreaSlug]: services
      }));
      showSnackbar("Failed to update service priority: " + error.message, "error");
    } finally {
      setIsSavingServices(false);
    }
  };

  const handleAddService = (workAreaSlug, service) => {
    const currentServices = servicesFormData[workAreaSlug] || [];
    if (currentServices.some(s => s.slug === service.slug)) return; // Prevent duplicates

    const newService = {
      slug: service.slug,
      name: service.name,
      priority: currentServices.length,
      is_featured: false,
      description: service.description || ''
    };

    const updatedServices = [...currentServices, newService];
    handleServicesFormChange(workAreaSlug, updatedServices);
  };

  const handleRemoveService = (workAreaSlug, serviceSlug) => {
    const services = servicesFormData[workAreaSlug] || [];
    const updatedServices = services.filter(s => s.slug !== serviceSlug);
    handleServicesFormChange(workAreaSlug, updatedServices);
  };

  const initializeServices = (workAreas) => {
    const newServicesFormData = {};
    const newOriginalServicesData = {};

    workAreas.forEach(workArea => {
      const servicesData = workArea.services ? workArea.services.map(service => ({
        ...service,
        priority: service.priority || 0,
        is_featured: service.is_featured || false
      })).sort((a, b) => a.priority - b.priority) : [];

      if (!servicesFormData[workArea.slug]) {
        newServicesFormData[workArea.slug] = servicesData;
        newOriginalServicesData[workArea.slug] = [...servicesData];
      } else {
        newServicesFormData[workArea.slug] = servicesFormData[workArea.slug];
        newOriginalServicesData[workArea.slug] = originalServicesData[workArea.slug];
      }
    });

    setServicesFormData(newServicesFormData);
    setOriginalServicesData(newOriginalServicesData);
  };

  // Service description save handler - Save immediately and show toast
  const handleServiceDescriptionSave = async (workAreaSlug, serviceSlug, newDescription) => {
    const services = servicesFormData[workAreaSlug] || [];
    const serviceIndex = services.findIndex(service => service.slug === serviceSlug);
    if (serviceIndex === -1) return;

    const updatedServices = [...services];
    updatedServices[serviceIndex] = {
      ...updatedServices[serviceIndex],
      description: newDescription
    };

    // Update state immediately
    setServicesFormData(prev => ({
      ...prev,
      [workAreaSlug]: updatedServices
    }));

    // Save immediately and show toast
    try {
      setIsSavingServices(true);
      const result = await saveServices(workAreaSlug, updatedServices);
      if (result.success) {
        showSnackbar("Service description updated successfully", "success");
        // Update original data to reflect saved state
        setOriginalServicesData(prev => ({
          ...prev,
          [workAreaSlug]: [...updatedServices]
        }));
        setServicesHasChanges(prev => ({
          ...prev,
          [workAreaSlug]: false
        }));
      } else {
        // Revert on failure
        setServicesFormData(prev => ({
          ...prev,
          [workAreaSlug]: services
        }));
        showSnackbar(result?.error || "Failed to update service description", "error");
      }
    } catch (error) {
      // Revert on failure
      setServicesFormData(prev => ({
        ...prev,
        [workAreaSlug]: services
      }));
      showSnackbar("Failed to update service description: " + error.message, "error");
    } finally {
      setIsSavingServices(false);
    }
  };

  return {
    servicesFormData,
    servicesHasChanges,
    isSavingServices,
    isSavingRef,
    handleServicesFormChange,
    handleSaveServices,
    handleCancelServices,
    handleServiceMoveUp,
    handleServiceMoveDown,
    handleAddService,
    handleRemoveService,
    initializeServices,
    handleServiceDescriptionSave
  };
};